import { Controller } from '@hotwired/stimulus';
import { Conversation } from '@elevenlabs/client';
import {
  appendVtvMessages,
  bootstrapVpeSession,
  fetchVpeChatThreadMount,
} from '../../utils/case_experience/vtv_chat_api';
import {
  normalizeNodeUid,
  patchDocumentUserState,
  registerErrorInRollbar,
} from '../../utils/case_experience/case_experience_in_rails';
import { updateDocumentUserState as legacyUpdateDocumentUserState } from '../../utils/case_experience_in_rails';

const MESSAGE_FLUSH_MS = 1500;
const APPEND_MAX_ATTEMPTS = 2;
const ELEVENLABS_WORKLET_PATHS = {
  rawAudioProcessor: '/elevenlabs/rawAudioProcessor.worklet.js',
  audioConcatProcessor: '/elevenlabs/audioConcatProcessor.worklet.js',
};

export default class extends Controller {
  static targets = ['toggle', 'status', 'textInputSection', 'hideDuringVoice'];

  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    versionId: Number,
    threadId: String,
    voiceAgentId: String,
    vtvEnabled: Boolean,
    feedbackSubmitted: Boolean,
  };

  connect() {
    if (!this.vtvEnabledValue || this.feedbackSubmittedValue) return;
    this._voiceActive = false;
    this._sessionThreadId = null;
    this._conversation = null;
    this._pendingMessages = {};
    this._pendingTimers = {};
    this._appendedKeys = new Set();
    this._appendChain = Promise.resolve();
    this._boundStopForInterviewEnd = () => {
      void this.stopVoiceSession();
    };
    this.element.addEventListener('vpe-chat-interview-ending', this._boundStopForInterviewEnd);
    this.setStatus('');
  }

  disconnect() {
    if (this._boundStopForInterviewEnd) {
      this.element.removeEventListener('vpe-chat-interview-ending', this._boundStopForInterviewEnd);
      this._boundStopForInterviewEnd = null;
    }
    void this.stopVoiceSession({ silent: true });
  }

  async toggleVoice(event) {
    event?.preventDefault();
    if (this.feedbackSubmittedValue) return;

    if (this._voiceActive) {
      await this.stopVoiceSession();
      return;
    }

    await this.startVoiceSession();
  }

  async startVoiceSession() {
    this.setStatus('Connecting…');
    this.setConnectingComposeLocked(true);
    if (this.hasToggleTarget) this.toggleTarget.disabled = true;

    try {
      const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      permissionStream.getTracks().forEach((track) => track.stop());
    } catch {
      await this.fallbackToText('Microphone permission is required for voice mode.');
      return;
    }

    try {
      const existingThreadId = this.getResolvedThreadId();
      const session = await bootstrapVpeSession({
        versionId: this.versionIdValue,
        threadId: existingThreadId,
      });

      const agentId = session.agent_id || this.voiceAgentIdValue;
      if (!agentId) {
        await this.fallbackToText('Voice is not configured for this case.');
        return;
      }

      this._sessionThreadId = session.thread_id;
      this.voiceAgentIdValue = String(agentId);
      await this.persistThreadIdToDus(session.thread_id);
      await this.ensureThreadMessagesMount(session.thread_id);

      this._conversation = await Conversation.startSession({
        agentId,
        connectionType: 'websocket',
        workletPaths: ELEVENLABS_WORKLET_PATHS,
        onConnect: () => {
          this._voiceActive = true;
          this.updateToggleUi();
          this.setStatus('Listening — speak to the patient.');
        },
        onDisconnect: () => {
          if (this._voiceActive) {
            void this.fallbackToText('Voice disconnected. Switched back to text.');
          }
        },
        onError: (message) => {
          registerErrorInRollbar('VTV session error', { message });
          void this.fallbackToText('Voice error. Switched back to text.');
        },
        onModeChange: ({ mode }) => {
          if (mode === 'speaking') {
            this.setStatus('Patient is speaking…');
            void this.flushPendingMessage('user');
          } else if (this._voiceActive) {
            this.setStatus('Listening — speak to the patient.');
            void this.flushPendingMessage('assistant');
          }
        },
        onMessage: (payload) => {
          this.queueTranscriptMessage(payload);
        },
      });
    } catch (error) {
      if (error?.status === 401) {
        await this.fallbackToText();
        return;
      }
      registerErrorInRollbar('Failed to start VTV session', { error: error.message });
      await this.fallbackToText(error.message || 'Could not start voice session.');
    }
  }

  async fallbackToText(statusMessage) {
    await this.stopVoiceSession({ silent: true });
    this.setStatus(statusMessage);
  }

  async stopVoiceSession({ silent = false } = {}) {
    if (this._pendingTimers) {
      clearTimeout(this._pendingTimers.user);
      clearTimeout(this._pendingTimers.assistant);
      this._pendingTimers = {};
    }

    // End audio first so a slow transcript flush cannot leave the patient talking.
    this._voiceActive = false;
    const conversation = this._conversation;
    this._conversation = null;

    if (conversation) {
      try {
        await conversation.endSession();
      } catch {
        // Session may already be closed.
      }
    }

    this.updateToggleUi();

    if (!silent) {
      this.setStatus('');
    }

    if (this._appendChain) {
      await this.flushPendingMessage('user');
      await this.flushPendingMessage('assistant');
    }
  }

  queueTranscriptMessage(payload) {
    const content = String(payload?.message || '').trim();
    if (!content) return;

    const role = this.normalizeRole(payload?.role || payload?.source);
    if (!role) return;

    this._pendingMessages[role] = {
      role,
      content,
      created_at: new Date().toISOString(),
      input_modality: 'vtv',
    };

    clearTimeout(this._pendingTimers[role]);
    this._pendingTimers[role] = setTimeout(() => {
      void this.flushPendingMessage(role);
    }, MESSAGE_FLUSH_MS);
  }

  async flushPendingMessage(role) {
    clearTimeout(this._pendingTimers[role]);

    const flush = async () => {
      const pending = this._pendingMessages[role];
      if (!pending || !this._sessionThreadId) return;

      const dedupeKey = `${pending.role}:${pending.content}:${pending.created_at}`;
      if (this._appendedKeys.has(dedupeKey)) {
        delete this._pendingMessages[role];
        return;
      }

      let lastError;
      for (let attempt = 1; attempt <= APPEND_MAX_ATTEMPTS; attempt += 1) {
        try {
          await appendVtvMessages({
            threadId: this._sessionThreadId,
            messages: [pending],
          });
          this._appendedKeys.add(dedupeKey);
          delete this._pendingMessages[role];
          return;
        } catch (error) {
          lastError = error;
        }
      }

      registerErrorInRollbar('Failed to append VTV transcript', {
        threadId: this._sessionThreadId,
        error: lastError?.message,
      });
    };

    this._appendChain = this._appendChain.then(flush, flush);
    return this._appendChain;
  }

  normalizeRole(role) {
    const value = String(role || '').toLowerCase();
    if (value === 'user') return 'user';
    if (value === 'assistant' || value === 'agent' || value === 'ai') return 'assistant';
    return null;
  }

  async ensureThreadMessagesMount(threadId) {
    if (document.getElementById(`thread_messages_${threadId}`)) return;

    const mountPoint =
      this.element.querySelector('#vpe_initial_messages_mount') ||
      this.element.querySelector('[data-case-experience--ai-assistant-chat-target="messagesContainer"]') ||
      this.element.querySelector('[data-ai-assistant-chat-target="messagesContainer"]');
    if (!mountPoint) return;

    try {
      const html = await fetchVpeChatThreadMount(threadId);
      mountPoint.outerHTML = html;
    } catch (error) {
      registerErrorInRollbar('Failed to mount VTV thread messages', {
        threadId,
        error: error.message,
      });
    }
  }

  async persistThreadIdToDus(threadId) {
    if (!this.documentIdValue || !this.userIdValue || !this.nodeUidValue) return;

    const payload = {
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body: {
        document_user_state: {
          state: {
            [normalizeNodeUid(this.nodeUidValue)]: {
              threadId: { value: threadId, updatedAt: Date.now() },
            },
          },
        },
      },
    };

    try {
      if (this.usesCaseExperienceDusApi()) {
        await patchDocumentUserState(payload);
      } else {
        await legacyUpdateDocumentUserState(payload);
      }
    } catch (error) {
      if (error?.status === 401) throw error;
      registerErrorInRollbar('Failed to persist VTV thread id to DUS', {
        threadId,
        error: error.message,
      });
      return;
    }

    this.threadIdValue = String(threadId);
    this.element.dataset.caseExperienceAiAssistantChatThreadIdValue = String(threadId);
    this.element.dataset.aiAssistantChatThreadIdValue = String(threadId);
  }

  usesCaseExperienceDusApi() {
    return (this.element.getAttribute('data-controller') || '').includes(
      'case-experience--ai-assistant-chat',
    );
  }

  getResolvedThreadId() {
    const fromForm = this.parseThreadIdFromForm();
    if (fromForm) return fromForm;
    const fromMount = this.parseThreadIdFromMessagesMount();
    if (fromMount) return fromMount;
    if (this.hasThreadIdValue && this.threadIdValue) return String(this.threadIdValue);
    return null;
  }

  parseThreadIdFromForm() {
    const form = this.element.querySelector('form[action*="/ai_assistant/threads/"]');
    if (!form?.action) return null;
    const match = form.action.match(/\/ai_assistant\/threads\/(\d+)\/messages/);
    return match ? match[1] : null;
  }

  parseThreadIdFromMessagesMount() {
    const el = this.element.querySelector('[id^="thread_messages_"]');
    const match = el?.id?.match(/^thread_messages_(\d+)$/);
    return match ? match[1] : null;
  }

  updateToggleUi() {
    if (!this.hasToggleTarget) return;

    this.toggleTarget.disabled = this.feedbackSubmittedValue;
    this.toggleTarget.setAttribute('aria-pressed', this._voiceActive ? 'true' : 'false');
    this.toggleTarget.setAttribute(
      'aria-label',
      this._voiceActive ? 'Switch to text' : 'Use voice',
    );

    // Classic controls are a text button; icon toggle keeps its SVG children.
    if (this.toggleTarget.dataset.vtvToggleStyle !== 'icon') {
      this.toggleTarget.textContent = this._voiceActive ? 'Switch to text' : 'Use voice';
    } else {
      const actions = this.toggleTarget.closest('.aqui-chat__input-actions');
      if (actions) {
        actions.classList.toggle('flex-1', this._voiceActive);
        actions.classList.toggle('w-full', this._voiceActive);
      }
    }

    this.hideDuringVoiceTargets.forEach((el) => {
      el.hidden = this._voiceActive;
    });

    if (!this._voiceActive) {
      this.setConnectingComposeLocked(false);
    }

    if (this.hasTextInputSectionTarget) {
      const toggleInsideSection = this.textInputSectionTarget.contains(this.toggleTarget);
      this.textInputSectionTarget.hidden = this._voiceActive && !toggleInsideSection;
    }
  }

  setConnectingComposeLocked(locked) {
    this.composeControlElements().forEach((el) => {
      if (locked) {
        if (el.dataset.vtvWasDisabled == null) {
          el.dataset.vtvWasDisabled = el.disabled ? '1' : '0';
        }
        el.disabled = true;
        return;
      }

      if (el.dataset.vtvWasDisabled == null) return;
      el.disabled = el.dataset.vtvWasDisabled === '1' || this.feedbackSubmittedValue;
      delete el.dataset.vtvWasDisabled;
    });
  }

  composeControlElements() {
    return this.element.querySelectorAll(
      [
        '[data-ai-assistant-speech-to-text-target="toggle"]',
        '[data-speech-to-text-target="toggle"]',
        '[data-case-experience--ai-assistant-chat-target="sendButton"]',
        '[data-ai-assistant-chat-target="sendButton"]',
      ].join(', '),
    );
  }

  setStatus(message) {
    if (!this.hasStatusTarget) return;

    const text = String(message || '').trim();
    const idle = text === '' || text === 'Text mode';
    this.statusTarget.textContent = idle ? '' : text;
    this.statusTarget.hidden = idle;
  }
}
